import { z } from 'zod';

import type {
  MenuCalendarEntry,
  MenuCalendarEntryCreateInput,
  MenuCalendarEntryUpdateInput,
} from './api';

const positiveIntString = z
  .string()
  .trim()
  .refine((value) => value === '' || (/^\d+$/.test(value) && Number(value) > 0), {
    message: 'Ingresá un número entero positivo',
  });

/**
 * Mirrors `apps/api` `createMenuCalendarEntrySchema`/
 * `updateMenuCalendarEntrySchema` (`menu-calendar.dto.ts`). `serviceDate`/
 * `menuId`/`positionId` stay in the schema so the edit form can render them
 * read-only, but `toUpdateInput` never sends them (`menu-calendar/spec.md` →
 * "Edición de entrada de calendario").
 */
export const menuCalendarEntryFormSchema = z.object({
  serviceDate: z.string().trim().min(1, 'La fecha de servicio es obligatoria'),
  menuId: z.string().uuid({ message: 'Seleccioná un menú' }),
  positionId: z.string().uuid({ message: 'Seleccioná una posición' }),
  maxQuantity: positiveIntString,
  notes: z.string().trim().max(500),
});

export type MenuCalendarEntryFormValues = z.infer<typeof menuCalendarEntryFormSchema>;

export const DEFAULT_MENU_CALENDAR_ENTRY_FORM_VALUES: MenuCalendarEntryFormValues = {
  serviceDate: '',
  menuId: '',
  positionId: '',
  maxQuantity: '',
  notes: '',
};

export function toMenuCalendarEntryFormValues(
  entry: MenuCalendarEntry,
): MenuCalendarEntryFormValues {
  return {
    serviceDate: entry.serviceDate,
    menuId: entry.menuId,
    positionId: entry.positionId,
    maxQuantity: entry.maxQuantity === null ? '' : String(entry.maxQuantity),
    notes: entry.notes ?? '',
  };
}

export function toMenuCalendarEntryCreateInput(
  values: MenuCalendarEntryFormValues,
): MenuCalendarEntryCreateInput {
  return {
    serviceDate: values.serviceDate,
    menuId: values.menuId,
    positionId: values.positionId,
    maxQuantity: values.maxQuantity === '' ? null : Number(values.maxQuantity),
    notes: values.notes === '' ? null : values.notes,
  };
}

export function toMenuCalendarEntryUpdateInput(
  values: MenuCalendarEntryFormValues,
): MenuCalendarEntryUpdateInput {
  return {
    maxQuantity: values.maxQuantity === '' ? null : Number(values.maxQuantity),
    notes: values.notes === '' ? null : values.notes,
  };
}
